import { writable } from "svelte/store";
import { variantToArtnr } from "$utils/variant-to-artnr.js";
import { PREFIX, getPersisted, persist } from "./store.utils.js";
import { addToCart } from "$lib/meta-ads/functions.js";
import { trAddToCart } from "$lib/tr/functions.js";
import type { Color, ProductResponse, Scallops, Size, Slides } from "$routes/server-api/product/types.js";
import { variantToMetaAdsProductId } from "$lib/tr/utils.js";

const CART_TYPES = {
    product: "product",
} as const;

export type CartType = {
    products: CartProduct[];
    discounts: string[];
};

export type CartProduct =
    | {
          cartItemType: typeof CART_TYPES.product;
          artnr: string;
          num: number;
          sample: boolean;
      }
    | {
          id: number;
          price?: number;
          size: Size;
          color: Color;
          scallop?: Scallops;
          slides?: Slides;
          type: ProductResponse["type"];
          sample: boolean;
      };

const getEmptyStoreObject = (): CartType => ({ products: [], discounts: [] });

const indexOfItem = (cart: CartType["products"], artnr: string) => cart.findIndex((item) => item.artnr === artnr);

const cartStore = () => {
    const lsKey = `${PREFIX}cart`;
    const { subscribe, set, update } = writable(getEmptyStoreObject());
    const hydrate = () => {
        const cart = getPersisted(lsKey);
        if (cart) {
            set(cart);
        }
    };
    hydrate();

    const updateNumItems = (artnr: string, sample: boolean, numFn: (x: number) => number | number) => {
        if (typeof numFn !== "function") {
            const val = numFn;
            numFn = () => val;
        }
        update((c = { products: [], discounts: [] }) => {
            c.products = c.products || [];
            const existingIndex = indexOfItem(c.products, artnr);
            if (existingIndex > -1) {
                c.products[existingIndex].num = Number(numFn(c.products[existingIndex].num));
            } else {
                c.products.unshift({
                    cartItemType: CART_TYPES.product,
                    artnr,
                    num: numFn(0),
                    sample,
                });
            }
            persist(lsKey, c);
            return c;
        });
    };

    const add = (variant: CartProduct, num: number) => {
        const artnr = variantToArtnr(variant);
        updateNumItems(artnr, variant.sample, (oldNum) => Number(oldNum) + Number(num));
        trAddToCart([{ id: variantToMetaAdsProductId(variant), quantity: num }]);
        addToCart([{ id: variantToMetaAdsProductId(variant), quantity: num }]);
    };
    const deleteItem = (artnr: string) => {
        update((c) => {
            const newProducts = c.products.filter((i) => !(i.artnr === artnr));
            const newCart = { ...c, products: newProducts };
            persist(lsKey, newCart);
            return newCart;
        });
    };
    const loadExternalCart = (externalCart: CartType) => {
        if (!externalCart || !externalCart.products) {
            return;
        }
        clear();
        externalCart.products.forEach((p) => add(p, p.num));
    };

    const clear = () => {
        update(() => {
            const c = getEmptyStoreObject();
            persist(lsKey, c);
            return c;
        });
    };

    const setDiscountCode = (code: string) => {
        update((c) => {
            // Only one at a time
            // Overwrite the existing data
            c.discounts = [code];
            persist(lsKey, c);
            return c;
        });
    };

    return { subscribe, add, delete: deleteItem, updateNumItems, loadExternalCart, setDiscountCode, clear };
};

export const cart = cartStore();
